// src/services/crossroadsGameService.ts
import { logger } from '../utils/logger';
// Service for managing Crossroads TCG multiplayer game sessions

import { supabase } from './supabase';
import { GameState } from '../components/games/crossroads/types';

export interface GameSession {
  id: string;
  status: 'waiting' | 'active' | 'completed' | 'abandoned';
  game_state: GameState;
  current_player_index: number;
  turn_number: number;
  current_phase: string | null;
  active_environment_card: string | null;
  winner_id: string | null;
  started_at: string;
  completed_at: string | null;
  last_action_at: string;
  created_at: string;
  updated_at: string;
}

export interface GamePlayer {
  id: string;
  game_id: string;
  user_id: string;
  player_index: number;
  deck_id: string;
  commander_health: number;
  is_winner: boolean;
  created_at: string;
}

export interface GameInvitation {
  matchId: string;
  opponentId: string;
  gameType: string;
  deckType: string;
}

/**
 * Create a new game session and send invitation via DM
 */
export async function createGameInvitation(
  matchId: string,
  opponentId: string,
  opponentName: string,
  gameType: string = 'Fire vs Water'
): Promise<{ success: boolean; gameId?: string; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // For now, we're just sending the invitation
    // The game session will be created when the invitation is accepted
    // Store invitation data in a temporary way (using DM metadata or separate table)

    return {
      success: true,
      gameId: 'pending' // Will be created on accept
    };
  } catch (error) {
    logger.error('Error creating game invitation:', error);
    return { success: false, error: 'Failed to create game invitation' };
  }
}

/**
 * Create a new game session
 */
export async function createGameSession(
  player1Id: string,
  player2Id: string,
  initialGameState: GameState
): Promise<{ success: boolean; gameId?: string; error?: string }> {
  try {
    // Create game record
    const { data: game, error: gameError } = await supabase
      .from('crossroads_games')
      .insert({
        status: 'active',
        game_state: initialGameState,
        current_player_index: initialGameState.currentPlayerIndex,
        turn_number: initialGameState.currentRound,
        current_phase: initialGameState.currentPhase,
        active_environment_card: initialGameState.activeEnvironmentCard,
      })
      .select()
      .single();

    if (gameError || !game) {
      logger.error('Error creating game:', gameError);
      return { success: false, error: gameError?.message || 'Failed to create game' };
    }

    // Create player records
    // Note: In a real implementation, we'd need to get actual deck IDs
    // For now, we'll use placeholder UUIDs
    const dummyDeckId = '00000000-0000-0000-0000-000000000000';

    const { error: playersError } = await supabase
      .from('crossroads_game_players')
      .insert([
        {
          game_id: game.id,
          user_id: player1Id,
          player_index: 0,
          deck_id: dummyDeckId,
          commander_health: initialGameState.players[0].commander.currentHealth,
        },
        {
          game_id: game.id,
          user_id: player2Id,
          player_index: 1,
          deck_id: dummyDeckId,
          commander_health: initialGameState.players[1].commander.currentHealth,
        },
      ]);

    if (playersError) {
      logger.error('Error creating game players:', playersError);
      return { success: false, error: playersError.message };
    }

    return { success: true, gameId: game.id };
  } catch (error) {
    logger.error('Error in createGameSession:', error);
    return { success: false, error: 'Failed to create game session' };
  }
}

/**
 * Get a game session by ID
 */
export async function getGameSession(
  gameId: string
): Promise<{ success: boolean; game?: GameSession; error?: string }> {
  try {
    const { data: game, error } = await supabase
      .from('crossroads_games')
      .select('*')
      .eq('id', gameId)
      .single();

    if (error) {
      logger.error('Error fetching game:', error);
      return { success: false, error: error.message };
    }

    return { success: true, game };
  } catch (error) {
    logger.error('Error in getGameSession:', error);
    return { success: false, error: 'Failed to fetch game session' };
  }
}

/**
 * Update game state
 */
export async function updateGameState(
  gameId: string,
  gameState: GameState
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('crossroads_games')
      .update({
        game_state: gameState,
        current_player_index: gameState.currentPlayerIndex,
        turn_number: gameState.currentRound,
        current_phase: gameState.currentPhase,
        last_action_at: new Date().toISOString(),
      })
      .eq('id', gameId);

    if (error) {
      logger.error('Error updating game state:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in updateGameState:', error);
    return { success: false, error: 'Failed to update game state' };
  }
}

/**
 * Log a game action
 */
export async function logGameAction(
  gameId: string,
  actionType: string,
  actionData: any,
  turnNumber: number,
  phase: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { error } = await supabase
      .from('crossroads_game_actions')
      .insert({
        game_id: gameId,
        user_id: user.id,
        action_type: actionType,
        action_data: actionData,
        turn_number: turnNumber,
        phase,
      });

    if (error) {
      logger.error('Error logging game action:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in logGameAction:', error);
    return { success: false, error: 'Failed to log game action' };
  }
}

/**
 * Subscribe to game state changes
 */
export function subscribeToGameUpdates(
  gameId: string,
  callback: (payload: any) => void
) {
  const channel = supabase
    .channel(`game:${gameId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'crossroads_games',
        filter: `id=eq.${gameId}`,
      },
      (payload) => {
        callback(payload.new);
      }
    )
    .subscribe();

  return channel;
}

/**
 * Unsubscribe from game updates
 */
export async function unsubscribeFromGameUpdates(channel: any) {
  await supabase.removeChannel(channel);
}

/**
 * Complete a game
 */
export async function completeGame(
  gameId: string,
  winnerId: string | null
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('crossroads_games')
      .update({
        status: 'completed',
        winner_id: winnerId,
        completed_at: new Date().toISOString(),
      })
      .eq('id', gameId);

    if (error) {
      logger.error('Error completing game:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    logger.error('Error in completeGame:', error);
    return { success: false, error: 'Failed to complete game' };
  }
}

/**
 * Get active games for current user
 */
export async function getUserActiveGames(): Promise<{
  success: boolean;
  games?: GameSession[];
  error?: string;
}> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated', games: [] };
    }

    const { data: games, error } = await supabase
      .from('crossroads_games')
      .select(`
        *,
        crossroads_game_players!inner(user_id)
      `)
      .eq('crossroads_game_players.user_id', user.id)
      .eq('status', 'active')
      .order('last_action_at', { ascending: false });

    if (error) {
      logger.error('Error fetching user games:', error);
      return { success: false, error: error.message, games: [] };
    }

    return { success: true, games: games || [] };
  } catch (error) {
    logger.error('Error in getUserActiveGames:', error);
    return { success: false, error: 'Failed to fetch active games', games: [] };
  }
}

/**
 * Check for and abandon inactive games
 * Games with no action for TIMEOUT_MINUTES are marked as abandoned
 */
const TIMEOUT_MINUTES = 15;

export async function checkAndAbandonInactiveGames(): Promise<{
  success: boolean;
  abandonedCount?: number;
  error?: string;
}> {
  try {
    const timeoutDate = new Date();
    timeoutDate.setMinutes(timeoutDate.getMinutes() - TIMEOUT_MINUTES);

    const { data, error } = await supabase
      .from('crossroads_games')
      .update({
        status: 'abandoned',
        completed_at: new Date().toISOString(),
      })
      .eq('status', 'active')
      .lt('last_action_at', timeoutDate.toISOString())
      .select();

    if (error) {
      logger.error('Error abandoning inactive games:', error);
      return { success: false, error: error.message };
    }

    return { success: true, abandonedCount: data?.length || 0 };
  } catch (error) {
    logger.error('Error in checkAndAbandonInactiveGames:', error);
    return { success: false, error: 'Failed to check inactive games' };
  }
}
